<template>
  <component :is="embedded ? 'div' : 'v-container'" :fluid="!embedded" class="intelligence-talent-insights pa-4">
    <div class="mb-8">
      <div class="d-flex align-center mb-2" style="gap: 12px">
        <v-icon size="32" color="#1E3A5F">mdi-lightbulb-on-outline</v-icon>
        <h2 class="text-h4 font-weight-bold ma-0">Talent Intelligence</h2>
      </div>
      <p class="text-body-2 ma-0" style="color: var(--resu-text-secondary, #9e9e9e)">
        Proactive insights surfaced from
        <strong style="color: var(--resu-text-primary, #1E3A5F)">{{ totalProfiles }}</strong>
        profiles — patterns that would never appear in a search
      </p>
    </div>

    <v-alert v-if="hasError" type="error" dismissible class="mb-4">
      {{ errorMessage }}
    </v-alert>

    <v-progress-linear v-if="isLoading" indeterminate color="#007AFF" class="mb-4" />

    <div v-if="summary" class="stat-cards-row mb-8">
      <div class="stat-card" style="border-top: 3px solid #8b5cf6">
        <div class="stat-icon"><v-icon color="#8b5cf6">mdi-star-outline</v-icon></div>
        <div class="stat-value" style="color: #8b5cf6">{{ summary.hidden_leaders_found || summary.hiddenLeadersFound || 0 }}</div>
        <div class="stat-label">Hidden Leaders</div>
      </div>
      <div class="stat-card" style="border-top: 3px solid #f59e0b">
        <div class="stat-icon"><v-icon color="#f59e0b">mdi-alert-outline</v-icon></div>
        <div class="stat-value" style="color: #f59e0b">{{ summary.flight_risks_detected || summary.flightRisksDetected || 0 }}</div>
        <div class="stat-label">Plateau Alerts</div>
      </div>
      <div class="stat-card" style="border-top: 3px solid #ef4444">
        <div class="stat-icon"><v-icon color="#ef4444">mdi-chart-bar</v-icon></div>
        <div class="stat-value" style="color: #ef4444">{{ summary.succession_gaps || summary.successionGaps || 0 }}</div>
        <div class="stat-label">Succession Gaps</div>
      </div>
      <div class="stat-card" style="border-top: 3px solid #3b82f6">
        <div class="stat-icon"><v-icon color="#3b82f6">mdi-puzzle-outline</v-icon></div>
        <div class="stat-value" style="color: #3b82f6">{{ summary.top_emerging_skills || summary.topEmergingSkills || 0 }}</div>
        <div class="stat-label">Emerging Skills</div>
      </div>
      <div class="stat-card" style="border-top: 3px solid #14b8a6">
        <div class="stat-icon"><v-icon color="#14b8a6">mdi-swap-horizontal</v-icon></div>
        <div class="stat-value" style="color: #14b8a6">{{ summary.cross_pollination_opportunities || summary.crossPollinationOpportunities || 0 }}</div>
        <div class="stat-label">Cross-Pollination</div>
      </div>
    </div>

    <!-- Hidden Leaders Section -->
    <div class="collapsible-section mb-6" style="border-left: 4px solid #8b5cf6">
      <button class="section-header" @click="toggleSection('hiddenLeaders')">
        <div>
          <div class="d-flex align-center" style="gap: 10px; margin-bottom: 4px">
            <v-icon color="#8b5cf6">mdi-star-outline</v-icon>
            <span class="section-title">Hidden Leaders</span>
            <span class="section-badge" style="background: rgba(139,92,246,0.12); color: #8b5cf6">
              {{ hiddenLeaders.length }}
            </span>
            <button class="method-info-btn" @click.stop="toggleMethodology('hiddenLeaders')" title="How is this calculated?">
              <v-icon small color="#9e9e9e">mdi-information-outline</v-icon>
            </button>
          </div>
          <div class="section-desc">Professionals without leadership titles who demonstrate strong leadership evidence</div>
        </div>
        <span class="section-arrow" :class="{ open: openSections.hiddenLeaders }">▼</span>
      </button>
      <div v-if="methodologyOpen.hiddenLeaders" class="methodology-panel" style="border-top-color: rgba(139,92,246,0.2)">
        <div class="methodology-header"><v-icon small color="#8b5cf6" class="mr-1">mdi-flask-outline</v-icon> How this is calculated</div>
        <p>Identifies employees without leadership titles (Manager, Director, VP, C-Suite) who show leadership evidence in their STAR achievements — mentoring, cross-team coordination, initiative ownership, and decision-making patterns. Based entirely on documented profile data, not AI inference.</p>
      </div>
      <div v-if="openSections.hiddenLeaders" class="section-content">
        <div v-if="hiddenLeaders.length === 0" class="text-center py-4" style="color: var(--resu-text-secondary, #9e9e9e)">No hidden leaders detected</div>
        <div v-else class="card-grid">
          <div v-for="(h, i) in hiddenLeaders" :key="i" class="detail-card" style="background: rgba(139,92,246,0.05); border-color: rgba(139,92,246,0.2)">
            <div class="d-flex justify-space-between align-start mb-2">
              <div>
                <a class="profile-link" @click.prevent="viewProfile(h.profile_id || h.profileId)">{{ h.name }}</a>
                <div class="text-caption" style="color: var(--resu-text-secondary, #9e9e9e); margin-top: 4px">{{ h.current_title || h.currentTitle }}</div>
              </div>
              <span class="seniority-badge" :style="seniorityStyle(h.seniority)">{{ h.seniority }}</span>
            </div>

            <div v-if="(h.skills || []).length > 0" class="d-flex flex-wrap mb-2" style="gap: 4px">
              <span v-for="(sk, j) in h.skills" :key="j" class="skill-chip" style="background: rgba(139,92,246,0.15); color: #a78bfa">{{ sk }}</span>
            </div>

            <div v-if="(h.leadership_signals || h.leadershipSignals || []).length > 0" class="mb-2">
              <div class="signal-label" style="color: #a78bfa">Leadership Signals</div>
              <div v-for="(sig, j) in (h.leadership_signals || h.leadershipSignals || [])" :key="'ls'+j" class="signal-item">
                <span class="signal-arrow">→</span>
                {{ typeof sig === 'string' ? sig : JSON.stringify(sig) }}
              </div>
            </div>

            <div v-if="(h.impact_signals || h.impactSignals || []).length > 0" class="mb-2">
              <div class="signal-label" style="color: #22c55e">Impact Signals</div>
              <div v-for="(sig, j) in (h.impact_signals || h.impactSignals || [])" :key="'is'+j" class="signal-item">
                <span class="signal-arrow">◆</span>
                {{ typeof sig === 'string' ? sig : JSON.stringify(sig) }}
              </div>
            </div>

            <div class="recommendation-box" style="background: rgba(139,92,246,0.08)">
              {{ h.recommendation }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Promotion Plateau Alerts Section -->
    <div class="collapsible-section mb-6" style="border-left: 4px solid #f59e0b">
      <button class="section-header" @click="toggleSection('plateauAlerts')">
        <div>
          <div class="d-flex align-center" style="gap: 10px; margin-bottom: 4px">
            <v-icon color="#f59e0b">mdi-alert-outline</v-icon>
            <span class="section-title">Promotion Plateau Alerts</span>
            <span class="section-badge" style="background: rgba(245,158,11,0.12); color: #f59e0b">
              {{ promotionPlateaus.length }}
            </span>
            <button class="method-info-btn" @click.stop="toggleMethodology('plateauAlerts')" title="How is this calculated?">
              <v-icon small color="#9e9e9e">mdi-information-outline</v-icon>
            </button>
          </div>
          <div class="section-desc">High performers with significant achievements but limited career progression</div>
        </div>
        <span class="section-arrow" :class="{ open: openSections.plateauAlerts }">▼</span>
      </button>
      <div v-if="methodologyOpen.plateauAlerts" class="methodology-panel" style="border-top-color: rgba(245,158,11,0.2)">
        <div class="methodology-header"><v-icon small color="#f59e0b" class="mr-1">mdi-flask-outline</v-icon> How this is calculated</div>
        <p>Flags employees with a high ratio of documented achievements to role changes. For example, someone with 7+ achievements but only 1-2 job title changes may be a high performer who hasn't been promoted — a potential retention risk. Risk level (Medium/High) is based on the severity of this imbalance. All data comes from the employee's work timeline and STAR achievements in their profile.</p>
      </div>
      <div v-if="openSections.plateauAlerts" class="section-content">
        <div v-if="promotionPlateaus.length === 0" class="text-center py-4" style="color: var(--resu-text-secondary, #9e9e9e)">No promotion plateaus detected</div>
        <div v-else class="card-grid">
          <div v-for="(f, i) in promotionPlateaus" :key="i" class="detail-card" style="background: rgba(245,158,11,0.05); border-color: rgba(245,158,11,0.2)">
            <div class="d-flex justify-space-between align-start mb-2">
              <div>
                <a class="profile-link" @click.prevent="viewProfile(f.profile_id || f.profileId)">{{ f.name }}</a>
                <div class="text-caption" style="color: var(--resu-text-secondary, #9e9e9e); margin-top: 4px">{{ f.current_title || f.currentTitle }}</div>
              </div>
              <span class="risk-badge" :style="riskStyle(f.risk_level || f.riskLevel)">
                {{ f.risk_level || f.riskLevel }}
              </span>
            </div>

            <div class="d-flex mb-2" style="gap: 16px">
              <div class="text-center">
                <div style="font-size: 22px; font-weight: 700; color: #f59e0b">{{ f.achievements_count || f.achievementsCount || 0 }}</div>
                <div class="counter-label">Achievements</div>
              </div>
              <div class="text-center">
                <div style="font-size: 22px; font-weight: 700; color: var(--resu-text-secondary, #9e9e9e)">{{ f.role_changes || f.roleChanges || 0 }}</div>
                <div class="counter-label">Role Changes</div>
              </div>
              <div class="text-center">
                <div style="font-size: 22px; font-weight: 700; color: #22c55e">{{ f.impact_achievements || f.impactAchievements || 0 }}</div>
                <div class="counter-label">Impact Hits</div>
              </div>
            </div>

            <div v-if="(f.skills || []).length > 0" class="d-flex flex-wrap mb-2" style="gap: 4px">
              <span v-for="(sk, j) in f.skills" :key="j" class="skill-chip" style="background: rgba(245,158,11,0.12); color: #fbbf24">{{ sk }}</span>
            </div>

            <div class="insight-box" style="background: rgba(245,158,11,0.08)">
              {{ f.insight }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Succession Pipeline Gaps Section -->
    <div class="collapsible-section mb-6" style="border-left: 4px solid #ef4444">
      <button class="section-header" @click="toggleSection('successionGaps')">
        <div>
          <div class="d-flex align-center" style="gap: 10px; margin-bottom: 4px">
            <v-icon color="#ef4444">mdi-chart-bar</v-icon>
            <span class="section-title">Succession Pipeline Gaps</span>
            <span class="section-badge" style="background: rgba(239,68,68,0.12); color: #ef4444">
              {{ successionGaps.length }}
            </span>
            <button class="method-info-btn" @click.stop="toggleMethodology('successionGaps')" title="How is this calculated?">
              <v-icon small color="#9e9e9e">mdi-information-outline</v-icon>
            </button>
          </div>
          <div class="section-desc">Seniority levels with insufficient bench depth for succession planning</div>
        </div>
        <span class="section-arrow" :class="{ open: openSections.successionGaps }">▼</span>
      </button>
      <div v-if="methodologyOpen.successionGaps" class="methodology-panel" style="border-top-color: rgba(239,68,68,0.2)">
        <div class="methodology-header"><v-icon small color="#ef4444" class="mr-1">mdi-flask-outline</v-icon> How this is calculated</div>
        <p>Compares headcount at each seniority level against the level below it to measure bench depth. A healthy succession ratio is 3:1 (three potential successors for each seat), per SHRM/Bersin recommended minimums. Ratios below this threshold are flagged — "Critical" when the ratio is 1:1 or worse, "Warning" when below benchmark. All data is derived from the organizational hierarchy mapped from employee job titles.</p>
      </div>
      <div v-if="openSections.successionGaps" class="section-content">
        <div v-if="successionGaps.length === 0" class="text-center py-4" style="color: var(--resu-text-secondary, #9e9e9e)">No succession data available</div>
        <div v-else style="display: grid; gap: 16px">
          <div v-for="(g, i) in successionGaps" :key="i" class="detail-card" :style="successionCardStyle(g)">
            <div class="d-flex justify-space-between align-center mb-3">
              <div class="d-flex align-center" style="gap: 10px">
                <span class="seniority-badge" :style="seniorityStyle(g.level)">{{ g.level }}</span>
                <span style="font-size: 14px; color: var(--resu-text-secondary, #9e9e9e)">→</span>
                <span class="seniority-badge" :style="seniorityStyle(g.bench_level || g.benchLevel)">{{ g.bench_level || g.benchLevel }}</span>
              </div>
              <span class="risk-badge" :style="severityStyle(g.severity)">{{ g.severity }}</span>
            </div>

            <div class="d-flex mb-3" style="gap: 24px">
              <div>
                <span style="font-size: 22px; font-weight: 700">{{ g.current_count || g.currentCount || g.seats || 0 }}</span>
                <span class="counter-label ml-1">{{ g.level }}</span>
              </div>
              <div>
                <span style="font-size: 22px; font-weight: 700">{{ g.bench_count || g.benchCount || g.bench || 0 }}</span>
                <span class="counter-label ml-1">{{ g.bench_level || g.benchLevel }}</span>
              </div>
              <div>
                <span style="font-size: 22px; font-weight: 700" :style="{ color: successionBarColor(g) }">{{ g.ratio }}:1</span>
                <span class="counter-label ml-1">ratio</span>
              </div>
            </div>

            <div class="mb-3">
              <div class="d-flex justify-space-between mb-1" style="font-size: 11px; color: var(--resu-text-secondary, #9e9e9e)">
                <span>Bench Depth Ratio</span>
                <span>Benchmark: 3:1</span>
              </div>
              <div class="bar-track">
                <div class="bar-fill" :style="{ width: benchBarWidth(g) + '%', background: successionBarColor(g) }"></div>
                <div class="benchmark-line"></div>
              </div>
            </div>

            <div style="font-size: 12px; color: var(--resu-text-secondary, #9e9e9e); line-height: 1.5">
              {{ g.insight }}
              <div style="font-size: 11px; margin-top: 6px; font-style: italic; opacity: 0.8">
                Industry benchmark: 3:1 bench-to-seat ratio (SHRM/Bersin recommended minimum)
              </div>
            </div>

            <div v-if="(g.functional_breakdown || g.functionalBreakdown || []).length > 0" class="mt-3">
              <button class="func-breakdown-toggle" @click.stop="toggleFuncBreakdown(i)">
                <span style="font-size: 12px; font-weight: 600; color: var(--resu-text-secondary, #9e9e9e)">By Function</span>
                <span class="section-badge" style="background: rgba(239,68,68,0.1); color: #ef4444; font-size: 10px">
                  {{ (g.functional_breakdown || g.functionalBreakdown || []).length }}
                </span>
                <span class="section-arrow" :class="{ open: funcBreakdownOpen[i] }" style="font-size: 10px; color: var(--resu-text-secondary, #9e9e9e)">▼</span>
              </button>
              <div v-if="funcBreakdownOpen[i]" class="func-breakdown-rows">
                <div
                  v-for="(fb, fi) in (g.functional_breakdown || g.functionalBreakdown || [])"
                  :key="fi"
                  class="func-row"
                  :style="funcRowStyle(fb)"
                  @click="navigateSuccessionFunc(g.level, fb.function)"
                >
                  <div class="func-row-name">{{ fb.function }}</div>
                  <div class="func-row-counts">
                    <span>{{ fb.current_count || fb.currentCount || 0 }} → {{ fb.bench_count || fb.benchCount || 0 }}</span>
                  </div>
                  <div class="func-row-ratio" :style="{ color: funcSeverityColor(fb.severity) }">
                    {{ fb.ratio }}:1
                  </div>
                  <span class="func-severity-dot" :style="{ background: funcSeverityColor(fb.severity) }" :title="fb.severity"></span>
                </div>
              </div>
            </div>

            <div v-if="(g.current_holders || g.currentHolders || []).length > 0" class="d-flex flex-wrap mt-2" style="gap: 6px">
              <a
                v-for="(holder, j) in (g.current_holders || g.currentHolders || [])"
                :key="j"
                class="holder-chip"
                @click.prevent="viewProfile(holder.profile_id || holder.profileId)"
              >
                {{ holder.name }}
              </a>
            </div>

            <div class="mt-3 text-right">
              <v-btn
                small
                outlined
                color="#ef4444"
                @click="navigateToSuccessionForGap(g)"
              >
                <v-icon small left>mdi-account-switch</v-icon>
                Run Succession Analysis
              </v-btn>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Emerging Skills Section -->
    <div class="collapsible-section mb-6" style="border-left: 4px solid #3b82f6">
      <button class="section-header" @click="toggleSection('skillsEmergence')">
        <div>
          <div class="d-flex align-center" style="gap: 10px; margin-bottom: 4px">
            <v-icon color="#3b82f6">mdi-puzzle-outline</v-icon>
            <span class="section-title">Emerging Skills</span>
            <span class="section-badge" style="background: rgba(59,130,246,0.12); color: #3b82f6">
              {{ skillsEmergence.length }}
            </span>
            <button class="method-info-btn" @click.stop="toggleMethodology('skillsEmergence')" title="How is this calculated?">
              <v-icon small color="#9e9e9e">mdi-information-outline</v-icon>
            </button>
          </div>
          <div class="section-desc">Skills gaining traction across your talent pool — emerging capabilities and growth areas</div>
        </div>
        <span class="section-arrow" :class="{ open: openSections.skillsEmergence }">▼</span>
      </button>
      <div v-if="methodologyOpen.skillsEmergence" class="methodology-panel" style="border-top-color: rgba(59,130,246,0.2)">
        <div class="methodology-header"><v-icon small color="#3b82f6" class="mr-1">mdi-flask-outline</v-icon> How this is calculated</div>
        <p>Counts skill occurrences across all employee profiles, broken down by seniority level. Skills are extracted from each person's skills lists and STAR achievement descriptions. The bar chart shows the most prevalent skills; seniority dots show distribution across career levels. The "Rarest Skills" sub-section highlights capabilities held by very few people — potential single points of failure if those skills are critical to your business.</p>
      </div>
      <div v-if="openSections.skillsEmergence" class="section-content">
        <div v-if="skillsEmergence.length === 0 && !skillsLandscape" class="text-center py-4" style="color: var(--resu-text-secondary, #9e9e9e)">No skills data available</div>
        <div v-else>
          <div style="display: grid; gap: 6px">
            <div v-for="(sk, i) in skillsEmergence" :key="i" class="d-flex align-center" style="gap: 12px">
              <div class="skill-name-label">{{ sk.skill || sk.name }}</div>
              <div class="d-flex align-center" style="flex: 1; gap: 8px">
                <div class="skill-bar-track">
                  <div class="skill-bar-fill" :style="skillBarStyle(sk, i)">
                    <span class="skill-bar-text">{{ sk.count }} ({{ sk.percentage || 0 }}%)</span>
                  </div>
                </div>
                <div class="d-flex" style="gap: 3px; flex-shrink: 0">
                  <span
                    v-for="(cnt, level) in (sk.by_seniority || sk.bySeniority || {})"
                    :key="level"
                    :title="`${level}: ${cnt}`"
                    class="seniority-dot"
                    :style="{ background: seniorityColors[level] || '#666', opacity: Math.min(cnt / 5, 1) * 0.7 + 0.3 }"
                  ></span>
                </div>
              </div>
            </div>
          </div>

          <div class="d-flex flex-wrap mt-4" style="gap: 12px">
            <div v-for="(color, level) in seniorityColors" :key="level" class="d-flex align-center" style="gap: 4px; font-size: 11px; color: var(--resu-text-secondary, #9e9e9e)">
              <span class="seniority-dot" :style="{ background: color }"></span>
              {{ level }}
            </div>
          </div>

          <div v-if="rarestSkills.length > 0" class="mt-6">
            <div class="d-flex align-center mb-3" style="gap: 8px">
              <v-icon color="#ef4444" size="18">mdi-alert-decagram-outline</v-icon>
              <span style="font-size: 15px; font-weight: 700">Rarest Skills — Potential Capability Gaps</span>
            </div>
            <p style="font-size: 13px; color: var(--resu-text-secondary, #9e9e9e); margin: 0 0 14px 0; line-height: 1.5">
              Skills held by very few people. If these are critical to your business, this signals a vulnerability.
            </p>
            <div class="rarest-grid">
              <div v-for="(sk, i) in rarestSkills" :key="i" class="rarest-card" :style="rarestCardStyle(sk)">
                <div>
                  <div style="font-size: 13px; font-weight: 600">{{ sk.skill || sk.name }}</div>
                  <div style="font-size: 11px; color: var(--resu-text-secondary, #9e9e9e)">
                    {{ sk.count }} {{ sk.count === 1 ? 'person' : 'people' }} ({{ sk.percentage || 0 }}%)
                  </div>
                </div>
                <span class="criticality-badge" :style="criticalityStyle(sk.criticality)">
                  {{ criticalityLabel(sk.criticality) }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Cross-Pollination Section -->
    <div class="collapsible-section mb-6" style="border-left: 4px solid #14b8a6">
      <button class="section-header" @click="toggleSection('crossPollination')">
        <div>
          <div class="d-flex align-center" style="gap: 10px; margin-bottom: 4px">
            <v-icon color="#14b8a6">mdi-swap-horizontal</v-icon>
            <span class="section-title">Cross-Pollination Opportunities</span>
            <span class="section-badge" style="background: rgba(20,184,166,0.12); color: #14b8a6">
              {{ crossPollination.length }}
            </span>
            <button class="method-info-btn" @click.stop="toggleMethodology('crossPollination')" title="How is this calculated?">
              <v-icon small color="#9e9e9e">mdi-information-outline</v-icon>
            </button>
          </div>
          <div class="section-desc">Skills that span multiple functional areas — potential for cross-functional collaboration</div>
        </div>
        <span class="section-arrow" :class="{ open: openSections.crossPollination }">▼</span>
      </button>
      <div v-if="methodologyOpen.crossPollination" class="methodology-panel" style="border-top-color: rgba(20,184,166,0.2)">
        <div class="methodology-header"><v-icon small color="#14b8a6" class="mr-1">mdi-flask-outline</v-icon> How this is calculated</div>
        <p>Scans every employee's skills and maps them against their functional area (department or team). When a skill appears across 3 or more different functional areas, it is flagged as a cross-pollination opportunity. The people counts and department breakdowns are real tallies from profile data — for example, "Strategic Thinking" showing 9 functions and 39 people means 39 employees across 9 different departments have that skill documented. Updates automatically when profiles change.</p>
      </div>
      <div v-if="openSections.crossPollination" class="section-content">
        <div v-if="crossPollination.length === 0" class="text-center py-4" style="color: var(--resu-text-secondary, #9e9e9e)">No cross-pollination data available</div>
        <div v-else class="card-grid">
          <div v-for="(cp, i) in crossPollination" :key="i" class="detail-card" style="background: rgba(20,184,166,0.05); border-color: rgba(20,184,166,0.2)">
            <div class="d-flex justify-space-between align-start mb-2">
              <div style="font-size: 16px; font-weight: 700; color: #2dd4bf">{{ cp.skill || cp.areaA }}</div>
              <div class="d-flex" style="gap: 6px">
                <span class="section-badge" style="background: rgba(20,184,166,0.15); color: #14b8a6">
                  {{ cp.function_count || cp.functionCount || cp.department_count || cp.departmentCount || 0 }} functions
                </span>
                <span class="section-badge" style="background: rgba(59,130,246,0.15); color: #3b82f6">
                  {{ cp.total_people || cp.totalPeople || 0 }} people
                </span>
              </div>
            </div>

            <div v-if="cp.areaB" class="d-flex align-center mb-2" style="gap: 8px">
              <span class="text-body-2 font-weight-bold">{{ cp.areaA }}</span>
              <v-icon small color="#14b8a6">mdi-swap-horizontal</v-icon>
              <span class="text-body-2 font-weight-bold">{{ cp.areaB }}</span>
            </div>

            <div class="d-flex flex-wrap mb-3" style="gap: 4px">
              <span
                v-for="(cnt, dept) in (cp.departments || {})"
                :key="dept"
                class="dept-chip"
              >
                {{ dept }} <span style="color: var(--resu-text-secondary, #9e9e9e)">({{ cnt }})</span>
              </span>
            </div>

            <div v-if="(cp.sharedSkills || []).length > 0" class="d-flex flex-wrap mb-3" style="gap: 4px">
              <span v-for="skill in cp.sharedSkills" :key="skill" class="skill-chip" style="background: rgba(20,184,166,0.15); color: #2dd4bf">{{ skill }}</span>
            </div>

            <div v-if="(cp.sample_members || cp.sampleMembers || []).length > 0" class="mb-2">
              <div class="signal-label" style="color: var(--resu-text-secondary, #9e9e9e)">Sample Members</div>
              <div class="d-flex flex-wrap" style="gap: 4px">
                <a
                  v-for="(m, j) in (cp.sample_members || cp.sampleMembers || [])"
                  :key="j"
                  class="member-chip"
                  @click.prevent="viewProfile(m.profile_id || m.profileId)"
                >
                  {{ m.name }}
                </a>
              </div>
            </div>

            <div v-if="cp.insight" class="insight-box" style="background: rgba(20,184,166,0.06)">
              {{ cp.insight }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Emerging Skills Advisor -->
    <div class="collapsible-section mb-6" style="border-left: 4px solid #8b5cf6">
      <button class="section-header" @click="toggleSection('emergingSkills')">
        <div>
          <div class="d-flex align-center" style="gap: 10px; margin-bottom: 4px">
            <v-icon color="#8b5cf6">mdi-creation</v-icon>
            <span class="section-title">Emerging Skills Advisor</span>
            <button class="method-info-btn" @click.stop="toggleMethodology('emergingSkills')" title="How is this calculated?">
              <v-icon small color="#9e9e9e">mdi-information-outline</v-icon>
            </button>
          </div>
          <div class="section-desc">Ask questions about industry skills trends, emerging capabilities, and what your organization should be developing</div>
        </div>
        <span class="section-arrow" :class="{ open: openSections.emergingSkills }">▼</span>
      </button>
      <div v-if="methodologyOpen.emergingSkills" class="methodology-panel" style="border-top-color: rgba(139,92,246,0.2)">
        <div class="methodology-header"><v-icon small color="#8b5cf6" class="mr-1">mdi-flask-outline</v-icon> How this works</div>
        <p>Uses an AI language model (LLM) to analyze your talent pool composition against industry trends. The model receives a summary of your organization's skills distribution and profile data, then generates strategic recommendations. Responses are AI-generated opinions, not deterministic calculations — always verify recommendations with your own domain expertise and industry knowledge before making decisions.</p>
      </div>
      <div v-if="openSections.emergingSkills" class="section-content">
        <div class="d-flex flex-wrap mb-3" style="gap: 6px">
          <button
            v-for="(s, i) in advisorSuggestions"
            :key="i"
            class="suggestion-chip"
            :disabled="isLoadingAdvisor"
            @click="askSuggestion(s)"
          >
            {{ s }}
          </button>
        </div>

        <div class="d-flex" style="gap: 8px">
          <input
            v-model="advisorQuestion"
            class="advisor-input"
            placeholder="Ask about industry skills, trends, or what to invest in..."
            :disabled="isLoadingAdvisor"
            @keydown.enter="askAdvisor"
          />
          <button
            class="advisor-btn"
            :disabled="isLoadingAdvisor || !advisorQuestion.trim()"
            :style="{ background: isLoadingAdvisor ? '#9ca3af' : '#8b5cf6', opacity: !advisorQuestion.trim() ? 0.5 : 1 }"
            @click="askAdvisor"
          >
            {{ isLoadingAdvisor ? 'Thinking...' : 'Ask' }}
          </button>
        </div>

        <div v-if="isLoadingAdvisor" class="text-center py-4" style="color: var(--resu-text-secondary, #9e9e9e); font-size: 14px">
          <v-icon size="24" color="#8b5cf6" class="mb-2">mdi-creation</v-icon>
          <div>Analyzing industry trends and your talent pool...</div>
        </div>

        <div v-if="emergingSkillsAnswer && !isLoadingAdvisor" class="mt-4">
          <div class="advisor-answer" v-html="formatAdvisorAnswer(emergingSkillsAnswer)"></div>
          <div class="ai-disclaimer mt-3">
            <v-icon small color="#9e9e9e" class="mr-1">mdi-robot-outline</v-icon>
            <span>This analysis is generated by an AI model based on your organization's profile data and industry trends. Use as a starting point — verify recommendations with domain expertise before acting on them.</span>
          </div>
        </div>
      </div>
    </div>
  </component>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

const SENIORITY_COLORS = {
  'C-Suite': '#8b5cf6',
  'VP': '#6366f1',
  'Director': '#3b82f6',
  'Manager': '#14b8a6',
  'Senior': '#22c55e',
  'Mid-Level': '#9ca3af',
  'Junior': '#f59e0b'
};

const ADVISOR_SUGGESTIONS = [
  "What emerging skills should we prioritize for 2026?",
  "How does our skills mix compare to industry trends?",
  "What critical skills are we missing for AI transformation?",
  "Which skills are becoming obsolete in our industry?",
  "What upskilling programs should we invest in?"
];

export default {
  name: "IntelligenceTalentInsights",
  props: {
    embedded: { type: Boolean, default: false },
  },
  data() {
    return {
      openSections: {
        hiddenLeaders: false,
        plateauAlerts: false,
        successionGaps: false,
        skillsEmergence: false,
        crossPollination: false,
        emergingSkills: false,
      },
      advisorQuestion: "",
      seniorityColors: SENIORITY_COLORS,
      advisorSuggestions: ADVISOR_SUGGESTIONS,
      methodologyOpen: {
        hiddenLeaders: false,
        plateauAlerts: false,
        successionGaps: false,
        skillsEmergence: false,
        crossPollination: false,
        emergingSkills: false,
      },
      funcBreakdownOpen: {},
    };
  },
  computed: {
    ...mapState("intelligenceTalent", [
      "isLoading",
      "hasError",
      "errorMessage",
      "emergingSkillsAnswer",
      "isLoadingAdvisor",
    ]),
    ...mapGetters("intelligenceTalent", [
      "hiddenLeaders",
      "promotionPlateaus",
      "successionGaps",
      "skillsLandscape",
      "skillsEmergence",
      "rarestSkills",
      "crossPollination",
      "summary",
      "hasData",
    ]),
    totalProfiles() {
      const s = this.summary;
      if (!s) return 0;
      const val = s.total_profiles || s.totalProfiles || 0;
      return val.toLocaleString ? val.toLocaleString() : val;
    },
    maxSkillCount() {
      if (this.skillsEmergence.length > 0) {
        return this.skillsEmergence[0].count || 1;
      }
      return 1;
    },
  },
  mounted() {
    if (!this.hasData) {
      this.loadTalentIntelligence();
    }
    this.checkHashNavigation();
  },
  methods: {
    ...mapActions("intelligenceTalent", [
      "loadTalentIntelligence",
      "askEmergingSkillsAdvisor",
    ]),
    toggleSection(section) {
      this.openSections[section] = !this.openSections[section];
    },
    toggleMethodology(section) {
      this.methodologyOpen[section] = !this.methodologyOpen[section];
    },
    formatAdvisorAnswer(content) {
      if (!content) return "";
      let html = content;
      html = html.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
      html = html.replace(/^### (.+)$/gm, '<h3 class="advisor-h3">$1</h3>');
      html = html.replace(/^## (.+)$/gm, '<h3 class="advisor-h3">$1</h3>');
      html = html.replace(/^# (.+)$/gm, '<h3 class="advisor-h3">$1</h3>');
      html = html.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>");
      html = html.replace(/\*(.*?)\*/g, "<em>$1</em>");
      const lines = html.split("\n");
      let result = [];
      let inList = false;
      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const listMatch = line.match(/^\s*[-*]\s+(.+)/);
        const numMatch = line.match(/^\s*\d+[.)]\s+(.+)/);
        if (listMatch || numMatch) {
          if (!inList) { result.push("<ul>"); inList = true; }
          result.push("<li>" + (listMatch ? listMatch[1] : numMatch[1]) + "</li>");
        } else {
          if (inList) { result.push("</ul>"); inList = false; }
          if (line.trim() === "") {
            result.push("<br>");
          } else {
            result.push(line);
          }
        }
      }
      if (inList) result.push("</ul>");
      return result.join("\n");
    },
    checkHashNavigation() {
      const hash = window.location.hash.replace('#', '');
      const sectionMap = {
        'hidden-leaders': 'hiddenLeaders',
        'plateau-alerts': 'plateauAlerts',
        'succession-gaps': 'successionGaps',
        'succession': 'successionGaps',
        'skills-emergence': 'skillsEmergence',
        'skillsLandscape': 'skillsEmergence',
        'cross-pollination': 'crossPollination',
        'crossPollination': 'crossPollination',
        'hiddenLeaders': 'hiddenLeaders',
        'promotionPlateaus': 'plateauAlerts',
      };
      if (sectionMap[hash]) {
        this.openSections[sectionMap[hash]] = true;
      }
      const querySection = this.$route && this.$route.query && this.$route.query.section;
      if (querySection && sectionMap[querySection]) {
        this.openSections[sectionMap[querySection]] = true;
        this.$nextTick(() => {
          const el = this.$el.querySelector('.collapsible-section .section-header');
          if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
      }
    },
    viewProfile(profileId) {
      this.$router.push({ name: "IntelligenceProfile", params: { profileId }, query: { from: 'insights' } });
    },
    navigateToSuccessionForGap(gap) {
      const role = gap.level || gap.role || gap.title || '';
      this.$router.push({
        name: 'IntelligenceFindTalent',
        query: { tab: 'succession', role: role }
      });
    },
    toggleFuncBreakdown(index) {
      this.$set(this.funcBreakdownOpen, index, !this.funcBreakdownOpen[index]);
    },
    navigateSuccessionFunc(level, funcArea) {
      this.$router.push({
        name: 'IntelligenceFindTalent',
        query: { tab: 'succession', role: level, department: funcArea }
      });
    },
    funcSeverityColor(severity) {
      if (severity === 'Critical') return '#ef4444';
      if (severity === 'Warning') return '#f59e0b';
      return '#22c55e';
    },
    funcRowStyle(fb) {
      const color = this.funcSeverityColor(fb.severity);
      return {
        borderLeft: `3px solid ${color}`,
      };
    },
    seniorityStyle(level) {
      const color = SENIORITY_COLORS[level] || '#9ca3af';
      return {
        padding: '3px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        background: color + '20',
        color: color,
        whiteSpace: 'nowrap',
      };
    },
    riskStyle(riskLevel) {
      const isHigh = riskLevel === 'High';
      return {
        padding: '3px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '700',
        background: isHigh ? 'rgba(239,68,68,0.15)' : 'rgba(245,158,11,0.15)',
        color: isHigh ? '#ef4444' : '#f59e0b',
      };
    },
    severityStyle(severity) {
      const isCritical = severity === 'Critical';
      const color = isCritical ? '#ef4444' : '#f59e0b';
      return {
        padding: '3px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '700',
        background: color + '25',
        color: color,
      };
    },
    successionCardStyle(g) {
      const isCritical = g.severity === 'Critical';
      return {
        background: isCritical ? 'rgba(239,68,68,0.05)' : 'rgba(245,158,11,0.05)',
        borderColor: isCritical ? 'rgba(239,68,68,0.2)' : 'rgba(245,158,11,0.2)',
      };
    },
    successionBarColor(g) {
      return g.severity === 'Critical' ? '#ef4444' : '#f59e0b';
    },
    benchBarWidth(g) {
      const benchmarkRatio = 3.0;
      return Math.min((g.ratio / benchmarkRatio) * 100, 100);
    },
    skillBarStyle(sk, index) {
      const barWidth = Math.max((sk.count / this.maxSkillCount) * 100, 4);
      const hue = 220 - (index * 4);
      const barColor = `hsl(${hue}, 70%, 55%)`;
      return {
        width: barWidth + '%',
        background: `linear-gradient(90deg, ${barColor}, ${barColor}99)`,
      };
    },
    rarestCardStyle(sk) {
      const critColor = sk.criticality === 'critical' ? '#ef4444' : sk.criticality === 'warning' ? '#f59e0b' : '#6b7280';
      return {
        background: sk.criticality === 'critical' ? 'rgba(239,68,68,0.06)' : 'rgba(255,255,255,0.03)',
        border: `1px solid ${critColor}25`,
      };
    },
    criticalityStyle(criticality) {
      const critColor = criticality === 'critical' ? '#ef4444' : criticality === 'warning' ? '#f59e0b' : '#6b7280';
      return {
        padding: '2px 8px',
        borderRadius: '10px',
        fontSize: '10px',
        fontWeight: '700',
        background: critColor + '15',
        color: critColor,
        textTransform: 'uppercase',
      };
    },
    criticalityLabel(criticality) {
      if (criticality === 'critical') return 'Single Point';
      if (criticality === 'warning') return 'Thin';
      return 'Low';
    },
    askSuggestion(suggestion) {
      this.advisorQuestion = suggestion;
      this.askEmergingSkillsAdvisor(suggestion);
    },
    askAdvisor() {
      if (!this.advisorQuestion.trim()) return;
      this.askEmergingSkillsAdvisor(this.advisorQuestion);
    },
  },
};
</script>

<style scoped>
.intelligence-talent-insights {
  max-width: 1200px;
}

.stat-cards-row {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
}

.stat-card {
  flex: 1 1 160px;
  padding: 20px 16px;
  background: var(--resu-bg-card, #fff);
  border: 1px solid var(--resu-border, #e0e0e0);
  border-radius: 12px;
  text-align: center;
}

.stat-icon {
  font-size: 28px;
  margin-bottom: 4px;
}

.stat-value {
  font-size: 28px;
  font-weight: 700;
  margin-bottom: 2px;
}

.stat-label {
  font-size: 12px;
  color: var(--resu-text-secondary, #9e9e9e);
  font-weight: 500;
}

.collapsible-section {
  background: var(--resu-bg-card, #fff);
  border: 1px solid var(--resu-border, #e0e0e0);
  border-radius: 14px;
  overflow: hidden;
}

.section-header {
  width: 100%;
  padding: 18px 24px;
  background: transparent;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  text-align: left;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--resu-text-primary, #1E3A5F);
}

.section-badge {
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
}

.section-desc {
  font-size: 13px;
  color: var(--resu-text-secondary, #9e9e9e);
  padding-left: 32px;
}

.section-arrow {
  font-size: 18px;
  color: var(--resu-text-secondary, #9e9e9e);
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.section-arrow.open {
  transform: rotate(180deg);
}

.section-content {
  padding: 0 24px 24px;
}

.card-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(380px, 1fr));
  gap: 16px;
}

.detail-card {
  padding: 20px;
  border: 1px solid;
  border-radius: 12px;
}

.profile-link {
  color: var(--resu-blue, #007AFF);
  text-decoration: none;
  font-weight: 600;
  border-bottom: 1px dotted var(--resu-blue, #007AFF);
  cursor: pointer;
}

.profile-link:hover {
  opacity: 0.8;
}

.seniority-badge {
  display: inline-block;
}

.skill-chip {
  padding: 2px 8px;
  border-radius: 10px;
  font-size: 11px;
  font-weight: 500;
}

.signal-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.signal-item {
  font-size: 12px;
  color: var(--resu-text-secondary, #9e9e9e);
  margin-bottom: 2px;
  padding-left: 12px;
  position: relative;
}

.signal-arrow {
  position: absolute;
  left: 0;
}

.recommendation-box,
.insight-box {
  font-size: 12px;
  color: var(--resu-text-secondary, #9e9e9e);
  padding: 8px 12px;
  border-radius: 8px;
  line-height: 1.5;
  font-style: italic;
}

.counter-label {
  font-size: 11px;
  color: var(--resu-text-secondary, #9e9e9e);
}

.risk-badge {
  display: inline-block;
}

.bar-track {
  height: 8px;
  background: rgba(0,0,0,0.06);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
}

.bar-fill {
  height: 100%;
  border-radius: 4px;
  transition: width 0.5s ease;
}

.benchmark-line {
  position: absolute;
  right: 0;
  top: -4px;
  width: 1px;
  height: 16px;
  background: #22c55e;
  opacity: 0.5;
}

.holder-chip {
  font-size: 11px;
  padding: 3px 10px;
  background: rgba(0,0,0,0.03);
  border: 1px solid var(--resu-border, #e0e0e0);
  border-radius: 16px;
  color: var(--resu-blue, #007AFF);
  text-decoration: none;
  cursor: pointer;
}

.holder-chip:hover {
  opacity: 0.8;
}

.skill-name-label {
  width: 150px;
  font-size: 13px;
  font-weight: 500;
  text-align: right;
  flex-shrink: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.skill-bar-track {
  flex: 1;
  height: 24px;
  background: rgba(0,0,0,0.04);
  border-radius: 6px;
  overflow: hidden;
  position: relative;
}

.skill-bar-fill {
  height: 100%;
  border-radius: 6px;
  display: flex;
  align-items: center;
  padding-left: 8px;
  transition: width 0.5s ease;
}

.skill-bar-text {
  font-size: 11px;
  font-weight: 600;
  color: #fff;
  white-space: nowrap;
}

.seniority-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  display: inline-block;
}

.func-breakdown-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px 0;
  width: 100%;
}

.func-breakdown-rows {
  display: grid;
  gap: 4px;
  margin-top: 6px;
}

.func-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 6px 10px;
  border-radius: 6px;
  background: rgba(0,0,0,0.02);
  cursor: pointer;
  transition: background 0.15s;
  font-size: 12px;
}

.func-row:hover {
  background: rgba(0,0,0,0.05);
}

.func-row-name {
  flex: 1;
  font-weight: 600;
  color: var(--resu-text-primary, #1E3A5F);
}

.func-row-counts {
  font-size: 11px;
  color: var(--resu-text-secondary, #9e9e9e);
  white-space: nowrap;
}

.func-row-ratio {
  font-weight: 700;
  font-size: 12px;
  min-width: 36px;
  text-align: right;
}

.func-severity-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rarest-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 8px;
}

.rarest-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 10px;
}

.dept-chip {
  padding: 3px 10px;
  border-radius: 16px;
  font-size: 11px;
  font-weight: 500;
  background: rgba(0,0,0,0.04);
  border: 1px solid var(--resu-border, #e0e0e0);
}

.member-chip {
  font-size: 11px;
  padding: 3px 10px;
  background: rgba(20,184,166,0.1);
  border: 1px solid rgba(20,184,166,0.2);
  border-radius: 16px;
  color: #2dd4bf;
  text-decoration: none;
  cursor: pointer;
}

.member-chip:hover {
  opacity: 0.8;
}

.suggestion-chip {
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 500;
  border: 1px solid rgba(139,92,246,0.2);
  background: rgba(139,92,246,0.06);
  color: #a78bfa;
  cursor: pointer;
  transition: all 0.15s;
}

.suggestion-chip:disabled {
  cursor: wait;
}

.suggestion-chip:hover:not(:disabled) {
  background: rgba(139,92,246,0.12);
}

.advisor-input {
  flex: 1;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid var(--resu-border, #e0e0e0);
  background: var(--resu-bg-main, #f5f5f5);
  color: var(--resu-text-primary, #1E3A5F);
  font-size: 14px;
  outline: none;
}

.advisor-btn {
  padding: 10px 20px;
  border-radius: 8px;
  border: none;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}

.advisor-btn:disabled {
  cursor: wait;
}

.advisor-warning {
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(245,158,11,0.08);
  border: 1px solid rgba(245,158,11,0.2);
  font-size: 12px;
  color: #fbbf24;
  display: flex;
  align-items: center;
  gap: 8px;
}

.advisor-answer {
  padding: 16px 20px;
  border-radius: 10px;
  background: rgba(139,92,246,0.04);
  border: 1px solid rgba(139,92,246,0.15);
  font-size: 14px;
  color: var(--resu-text-primary, #1E3A5F);
  line-height: 1.8;
}

.advisor-answer >>> .advisor-h3 {
  font-size: 16px;
  font-weight: 700;
  color: var(--resu-text-primary, #1E3A5F);
  margin: 16px 0 8px 0;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(139,92,246,0.1);
}

.advisor-answer >>> .advisor-h3:first-child {
  margin-top: 0;
}

.advisor-answer >>> ul {
  margin: 8px 0;
  padding-left: 20px;
}

.advisor-answer >>> li {
  margin-bottom: 6px;
  line-height: 1.6;
}

.advisor-answer >>> strong {
  color: var(--resu-text-primary, #1E3A5F);
  font-weight: 600;
}

.method-info-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 2px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  opacity: 0.6;
  transition: opacity 0.15s;
}

.method-info-btn:hover {
  opacity: 1;
}

.methodology-panel {
  margin: 0 24px;
  padding: 14px 16px;
  background: rgba(0,0,0,0.02);
  border: 1px solid var(--resu-border, #e0e0e0);
  border-top: 2px solid;
  border-radius: 8px;
  margin-bottom: 12px;
}

.methodology-header {
  font-size: 12px;
  font-weight: 700;
  color: var(--resu-text-secondary, #9e9e9e);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
}

.methodology-panel p {
  font-size: 13px;
  color: var(--resu-text-secondary, #9e9e9e);
  line-height: 1.7;
  margin: 0;
}

.ai-disclaimer {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding: 10px 14px;
  border-radius: 8px;
  background: rgba(0,0,0,0.03);
  border: 1px solid var(--resu-border, #e0e0e0);
  font-size: 11px;
  color: var(--resu-text-secondary, #9e9e9e);
  line-height: 1.5;
  font-style: italic;
}
</style>

<style>
.theme--dark .intelligence-talent-insights {
  --resu-bg-card: #1e1e2e;
  --resu-bg-main: #2a2a3e;
  --resu-border: #444;
  --resu-text-primary: #90CAF9;
  --resu-text-secondary: #aaa;
  --resu-blue: #4da6ff;
}
.theme--dark .intelligence-talent-insights .ai-disclaimer {
  background: rgba(255,255,255,0.05);
}
</style>
